<template>
  <div v-if="!params.dontShowEmpty || typeof params.dontShowEmpty  === 'string' &&
  params.dontShowEmpty  === 'true' && items.length > 0">
    <v-select
      v-if="
        loading || (
          typeof params.forcefilter === 'string'
            ? params.forcefilter !== 'true'
            : items.length <= 10
        )
      "
      :outlined="disableOutlined ? false : !disabled"
      :filled="disabled"
      v-model="value"
      :hide-selected="false"
      :label="label"
      :persistent-hint="hint !== ''"
      :hint="hint"
      :hide-details="'auto'"
      :disabled="loading || disabled"
      :loading="loading"
      :multiple="params.multiple === 'true'"
      :items="items"
      :item-text="itemText"
      :item-value="valueAttribute"
      :clearable="!required && value != null"
      :rules="rules"
      :menu-props="{ bottom: true, offsetY: true }"
      @change="emitInputData"
    >
      <template v-if="$slots.required" v-slot:prepend-inner>
        <slot name="required"></slot>
      </template>
    </v-select>

    <v-autocomplete
      v-else
      :outlined="!disabled"
      :filled="disabled"
      v-model="value"
      ref="autocomplete"
      :hide-selected="params.multiple !== 'true' && items.length > 1"
      :label="label"
      :persistent-hint="hint !== ''"
      :hint="hint"
      :hide-details="false"
      :disabled="loading || disabled"
      :loading="loading"
      :items="items"
      :item-text="itemText"
      :item-value="valueAttribute"
      :multiple="params.multiple === 'true'"
      :clearable="!required && value != null"
      :rules="rules"
      @change="emitInputData"
    >
      <template v-if="$slots.required" v-slot:prepend-inner>
        <slot name="required"></slot>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>

/**
 * expected params format:
 *
 * {
 *  mode: <string> specifies component options loading mode ('remote' to make a search)
 *  target: <string> specifies service target [ 'backend', 'cges', 'CA' ],
 *  entity: <string> specifies service entity (I.E 'cnt', 'nr', ...),
 *  fields: <string> requested fields splitted by ',' sent into each search request
 *  lookandfeel: <Function(item)> parse item expression to render each result row
 * }
 */
export default {
  props: {
    label: String,
    hint: String,
    placeholder: String,
    disabled: Boolean,
    disableOutlined: Boolean,
    rules: {
      type: Array,
      default: () => [],
    },
    initialValue: {
      required: false,
    },
    inputData: Object,
    params: {
      type: Object,
      default: () => ({}),
    },
    required: {
      type: Boolean,
      default: () => false,
    },
    fromApprovalList: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      value: null,
      loading: false,
      items: [],
      searchArguments: {},
    };
  },

  watch: {
    globalContext() {
      if (this.isRemote) {
        const shouldUpdateComponent = ['apicges', 'filters']
          .filter((key) => this.params[key])
          .map((key) => [key, this.evalParam(this.params[key])])
          .some((neweval) => {
            const newchanges = JSON.stringify(neweval[1]);
            const oldchanges = JSON.stringify(this.searchArguments[neweval[0]]);
            // console.log('old', oldchanges, 'vs new', newchanges);
            return newchanges !== oldchanges;
          });

        if (shouldUpdateComponent) {
          console.log(`updating ${ this.$parent.alias } component...`);
          this.refreshItems();
          this.$forceUpdate();
        }
      }
    },
    'params.items': {
      deep: true,
      immediate: true,
      handler() {
        if (this.fromApprovalList) {
          this.refreshItems();
        }
      },
    },
  },

  methods: {
    async search() {
      this.loading = true;

      const {
        // target, entity, fields, lookandfeel, match,
        // target, entity, fields, filters, order, firstP = 0, sizeP = 10, apicges,
        target, entity, fields = 'id,name', apicges, filters, order, querysize,
      } = this.params;

      let evaluedApicges;
      if (apicges) {
        evaluedApicges = this.evalParam(apicges);
        this.searchArguments.apicges = evaluedApicges;
      }

      let evaluatedFilters;
      if (filters) {
        evaluatedFilters = this.evalParam(filters);
        this.searchArguments.filters = evaluatedFilters;
      }

      let result = null;
      try {
        result = await this.client.search({
          target,
          entity,
          fields,
          filters: evaluatedFilters,
          order,
          apicges: evaluedApicges,
          start: 0,
          size: querysize,
        });
      } catch (ex) {
        console.error('error during search request', ex);
        result = { list: [] }; // formatting empty result
      }

      this.loading = false;

      return result;
    },

    itemText(item) {
      const { lookandfeel } = this.params;

      if (typeof lookandfeel === 'function') {
        return lookandfeel({ item, value: this.value });
      }
      return this.isRemote ? item.name : item.label;
    },

    evalParam(param, v) {
      const str = typeof param === 'function' ? param({ value: v }) : param;
      try {
        const parsedJson = JSON.parse(str || '{}');
        const resultJson = {};
        Object.entries(parsedJson).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            resultJson[key] = value;
          }
        });
        return resultJson;
      } catch (ex) {
        return str;
      }
    },

    async refreshItems() {
      const { mode, items } = this.params;
      if (this.isRemote) {
        const result = await this.search();
        this.items = result.list;
      } else if (typeof items === 'object' && Object.keys(items).length > 0) {
        this.items = Object.entries(items)
          .map((entry) => ({
            ...entry[1],
            // if value is not provided we will use as value the item label
            value: entry[1].value || entry[1].label,
            id: entry[0],
          }));
      } else {
        throw new Error(`There's no options configured in select component (got mode=${ mode }, items=${ items }). Please check component configuration`);
      }

      if (this.items.length === 1) {
        this.value = this.valueAttributeItem(this.items[0]);
        this.$emit('update:inputData', {
          value: this.value,
          item: this.items[0],
        });
      }

      if (typeof this.params.groupby === 'string') {
        const groupBy = this.params.groupby;
        const { groupkeys = {} } = this.params;

        const ordenAssignee = this.$getConst('assigneeField.ordenAssignee');
        const groupbyAssignee = this.$getConst('assigneeField.groupbyAssignee');
        const element = this.$getConst('assigneeField.element');

        if (this.params.groupby === groupbyAssignee) {
          this.items.sort((a, b) => {
            const ordenA = ordenAssignee[a.codigo] || element;
            const ordenB = ordenAssignee[b.codigo] || element;

            return ordenA - ordenB;
          });
        }

        const groups = this.items.reduce((acum, curr) => {
          if (curr[groupBy] != null && !acum.includes(curr[groupBy])) {
            acum.push(curr[groupBy]);
          }
          return acum;
        }, []);

        this.items = groups
          .sort((g1, g2) => g1 - g2)
          .reduce((groupedItems, groupValue) => {
            const groupKey = typeof groupkeys[groupValue] === 'string' ? groupkeys[groupValue] : groupValue;
            const header = { header: groupKey, divider: true };
            const group = this.items.filter((item) => item[groupBy] === groupValue);
            if (group.length === 1 && groupedItems.some((item) => group[0].id === item.id)) {
              return groupedItems;
            }
            return groupedItems.concat([header].concat(group));
          }, []);
      }
    },

    emitInputData() {
      let { value } = this;
      const item = this.items.find((itm) => (this.params.value
        ? this.valueAttributeItem(itm)
        : itm[(this.isRemote ? 'id' : 'value')]) === value);

      if (this.params.valueasobject === 'true' && item != null) {
        value = { value, id: item.id };
      }
      this.$emit('update:inputData', {
        value,
        item,
      });
    },
  },

  computed: {
    isRemote() {
      const { mode } = this.params;
      return typeof mode === 'string' && mode === 'remote';
    },
    globalContext() {
      return this.$parent.expressionInterpreter.ctx;
    },
    valueAttribute() {
      if (typeof this.params.value === 'function') {
        return (item) => this.params.value({
          item,
        });
      }
      console.log('value attribute', this.params.value || (this.isRemote ? 'id' : 'value'));
      return this.params.value || (this.isRemote ? 'id' : 'value');
    },
    valueAttributeItem() {
      if (this.valueAttribute === 'value') {
        return (item) => item.value;
      }
      if (typeof this.params.value === 'function') {
        return (item) => this.params.value({ item });
      }
      const value = this.valueAttribute.split('.');
      return (item) => value.reduce((tot, curr) => tot[curr], item);
    },
  },

  async created() {
    this.client = this.$api.getClient('search');
    // initial items loading
    await this.refreshItems();
    this.defaultItem = !!this.$slots.required && this.items.length > 0 ? this.items[0] : this.value;
    const searchItem = (value) => (
      this.items.find((item) => this.valueAttributeItem(item) === value));
    if (this.initialValue != null && typeof this.initialValue !== 'undefined') {
      this.value = /^\+?(0|[1-9]\d*)$/.test(this.initialValue) ? parseInt(this.initialValue, 10) : this.initialValue;
      let itemSelect = searchItem(this.value);
      if (typeof itemSelect === 'undefined' && /^\+?(0|[1-9]\d*)$/.test(this.initialValue)) {
        this.value = String(this.value);
        itemSelect = searchItem(this.value);
      }
      this.$emit('update:inputData', {
        value: this.value,
        item: itemSelect,
      });
    }
  },

  beforeDestroy() {
    this.$emit('update:inputData', {});
  },

};
</script>

<style lang="scss">

  .v-list > .v-subheader {
    font-weight: bold;
    color: #27846D; /* primary color */
    text-transform: uppercase;
  }

  .v-list > .v-list-item{
    min-height: 32px
  }

  .v-list > .v-list-item > .v-list-item__content > .v-list-item__title {
    white-space: normal !important;
  }

</style>
